/** Asset kind from unified single-schema API (`type` / `vehicle_type`). */
export type ClientAssetType = string

export const ALL_ASSETS_TYPE_SLUG = 'all'

const BIKE_ASSET_TYPES = new Set(['bike', 'ebike', 'e-bike', 'scooter'])

const DEFAULT_TYPE_LABELS: Record<string, string> = {
  vehicle: 'vehicles',
  car: 'vehicles',
  bike: 'bike',
  cargo: 'cargo',
  truck: 'trucks',
  van: 'vans',
}

const DEFAULT_TYPE_ICONS: Record<string, string> = {
  bike: 'two_wheeler',
  cargo: 'local_shipping',
  truck: 'local_shipping',
  van: 'airport_shuttle',
  car: 'directions_car',
  vehicle: 'directions_car',
}

export function normalizeVehicleTypeSlug(raw: unknown): string {
  if (raw == null || String(raw).trim() === '') return 'vehicle'
  return String(raw).trim().toLowerCase().replace(/\s+/g, '_')
}

export function readClientAssetType(row: Record<string, unknown> | null | undefined): ClientAssetType {
  const raw = row?.vehicle_type ?? row?.type
  if (raw != null && String(raw).trim() !== '') return normalizeVehicleTypeSlug(raw)
  if (row?.go_metro_bike_id != null && row.go_metro_bike_id !== '') return 'bike'
  return 'vehicle'
}

export function isBikeAssetType(type: ClientAssetType): boolean {
  return BIKE_ASSET_TYPES.has(type)
}

export function isMotorVehicleAssetType(type: ClientAssetType): boolean {
  return !isBikeAssetType(type)
}

export function vehicleTypePillLabel(
  slug: string,
  catalog?: Array<{ slug: string; name?: string; label?: string; plural_label?: string }>,
): string {
  if (slug === ALL_ASSETS_TYPE_SLUG) return 'All assets'
  const fromCatalog = findVehicleTypeInCatalog(catalog, slug)
  const raw = fromCatalog?.plural_label ?? fromCatalog?.label ?? fromCatalog?.name
  if (raw && String(raw).trim()) return String(raw).trim()
  const normalized = normalizeVehicleTypeSlug(slug)
  return DEFAULT_TYPE_LABELS[normalized] ?? normalized.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export function vehicleTypeIconName(slug: string, catalogIcon?: string | null): string {
  if (catalogIcon && String(catalogIcon).trim()) return String(catalogIcon).trim()
  return DEFAULT_TYPE_ICONS[normalizeVehicleTypeSlug(slug)] ?? 'directions_car'
}

export function vehicleTypeRowLabel(
  slug: string,
  catalog?: Array<{ slug: string; name?: string; label?: string }>,
): string {
  const fromCatalog = findVehicleTypeInCatalog(catalog, slug)
  const raw = fromCatalog?.label ?? fromCatalog?.name
  if (raw && String(raw).trim()) return String(raw).trim()
  const normalized = normalizeVehicleTypeSlug(slug)
  const fallback = DEFAULT_TYPE_LABELS[normalized]
  if (fallback) return fallback.replace(/s$/, '')
  return normalized.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Match asset row type to a pill slug (handles car/vehicle aliases). */
export function assetMatchesTypeSlug(assetType: string, filterSlug: string): boolean {
  const asset = normalizeVehicleTypeSlug(assetType)
  const filter = normalizeVehicleTypeSlug(filterSlug)
  if (asset === filter) return true
  if ((filter === 'vehicle' || filter === 'car') && (asset === 'vehicle' || asset === 'car')) return true
  return false
}

export function findVehicleTypeInCatalog<T extends { slug: string }>(
  catalog: T[] | undefined,
  slug: string,
): T | undefined {
  const normalized = normalizeVehicleTypeSlug(slug)
  return catalog?.find((t) => normalizeVehicleTypeSlug(t.slug) === normalized)
}
